import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck, ArrowRight } from 'lucide-react'
import { moneyFromMinor, moneyToMinor, rateFromScaled, rateToScaled, percentFromScaled } from '../../../utils/UnitConversion.utils'
import api from '../../../../api/axios'
import { CountryProviderFeeDTO } from './Deposit'

type CheckoutStepProps = {
    provider: string | null
    country?: string | null
    selectedCountryEnum?: string | null
    setSelectedCountryEnum?: React.Dispatch<React.SetStateAction<string | null>>
    amountInputInUserCurrency?: string
    targetAmountInUsd: number
    setModalOpen: (open: boolean) => void
    kyc: string
    currency: string
    currencySymbol: string
    currencyRate: number
    eurRateScaled: number
    convertFromUSD?: (v: number) => number
    depositFees: CountryProviderFeeDTO[] | undefined
    minAmount?: number
    maxAmount?: number
}

const CheckoutStep: React.FC<CheckoutStepProps> = ({
                                                       provider,
                                                       country,
                                                       selectedCountryEnum,
                                                       amountInputInUserCurrency = '',
                                                       targetAmountInUsd,
                                                       setModalOpen,
                                                       kyc,
                                                       currency,
                                                       currencySymbol,
                                                       currencyRate,
                                                       eurRateScaled,
                                                       convertFromUSD = (v: number) => v * currencyRate,
                                                       depositFees,
                                                       minAmount,
                                                       maxAmount,
                                                   }) => {
    const [agreeTerms, setAgreeTerms] = useState(false)

    // Resolve active country code from country or selectedCountryEnum prop
    const activeCountry = country ?? selectedCountryEnum

    const selectedFee = depositFees?.find(
        f => (f.providerFeeType || (f as unknown as { name?: string }).name) === provider
    )
    const kycRequired = provider === 'OPEN_BANKING' && kyc !== 'APPROVED'

    let providerFeeFixed = 0
    let providerFeePercent = 0
    let rawFeePercent = 0
    let rawFxFeePercent = 0

    if (selectedFee) {
        providerFeeFixed = convertFromUSD(moneyFromMinor(selectedFee.fixedMinor)) || 0
        rawFeePercent = percentFromScaled(selectedFee.feeBps) || 0
        rawFxFeePercent = percentFromScaled(selectedFee.fxFeeBps) || 0
        providerFeePercent = rawFeePercent / 100
    }

    const fxFeePercent = rawFxFeePercent / 100
    const userAmount = Number.parseFloat((amountInputInUserCurrency || '').replace(',', '.')) || 0
    const displayedAmount = provider === 'OPEN_BANKING'
        ? (currency === 'EUR' ? userAmount : (currencyRate > 0 ? (userAmount / currencyRate) * rateFromScaled(eurRateScaled) : userAmount))
        : userAmount

    const activeSymbol = provider === 'OPEN_BANKING' ? '€' : currencySymbol

    const baseFee = displayedAmount * providerFeePercent + providerFeeFixed
    const fxFee = displayedAmount * fxFeePercent
    const total = displayedAmount + baseFee + fxFee

    const amountInUserCurrency = convertFromUSD(targetAmountInUsd)
    const isAmountValid =
        targetAmountInUsd > 0 &&
        (minAmount === undefined || amountInUserCurrency >= minAmount) &&
        (maxAmount === undefined || amountInUserCurrency <= maxAmount)

    // Checks if country code is missing when Stripe provider is selected
    const isCountryMissing = provider !== 'OPEN_BANKING' && !activeCountry

    const isSubmitDisabled = kycRequired || !agreeTerms || !isAmountValid || isCountryMissing

    const formatDisplay = (value: number) => (Math.round(value * 100) / 100).toFixed(2)

    return (
        <div className="flex flex-col gap-3.5">
            {kycRequired && (
                <div className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-medium">
                    You must complete{' '}
                    <Link to="/profile/kyc" onClick={() => setModalOpen(false)} className="underline font-bold">
                        KYC-Verification
                    </Link>{' '}
                    before proceeding with Open Banking.
                </div>
            )}

            {isCountryMissing && (
                <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-medium">
                    Please select a valid country before proceeding with this payment method.
                </div>
            )}

            <div className="flex flex-col gap-1.5 text-xs text-neutral-600 dark:text-neutral-300 bg-white dark:bg-neutral-900/40 p-3 rounded-xl border border-neutral-200/60 dark:border-neutral-800/60">
                <div className="flex justify-between items-center py-0.5">
                    <span>Deposit Balance</span>
                    <span className="font-semibold text-neutral-900 dark:text-white">
                        {activeSymbol}{formatDisplay(displayedAmount)}
                    </span>
                </div>

                <div className="flex justify-between items-center py-0.5 border-t border-neutral-100 dark:border-neutral-800/60">
                    <span>Processing Fee</span>
                    <span className="font-semibold text-neutral-900 dark:text-white">
                        {activeSymbol}{formatDisplay(baseFee)}
                    </span>
                </div>

                {rawFxFeePercent > 0 && (
                    <div className="flex justify-between items-center py-0.5 border-t border-neutral-100 dark:border-neutral-800/60">
                        <span>FX Fee ({rawFxFeePercent}%)</span>
                        <span className="font-semibold text-amber-500">
                            {activeSymbol}{formatDisplay(fxFee)}
                        </span>
                    </div>
                )}

                <div className="flex justify-between items-center pt-2 border-t border-neutral-200 dark:border-neutral-800 text-xs font-bold text-neutral-900 dark:text-white">
                    <span>Total Payable</span>
                    <span className="text-blue-500 text-sm">
                        {activeSymbol}{formatDisplay(total)}
                    </span>
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 pt-0.5">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={e => setAgreeTerms(e.target.checked)}
                        className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
                        I agree to the{' '}
                        <Link to="/tos" onClick={() => setModalOpen(false)} className="text-blue-500 hover:underline font-semibold">
                            Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" onClick={() => setModalOpen(false)} className="text-blue-500 hover:underline font-semibold">
                            Privacy Policy
                        </Link>
                    </span>
                </label>

                <button
                    type="button"
                    disabled={isSubmitDisabled}
                    onClick={async () => {
                        if (!provider || !isAmountValid || isCountryMissing) return

                        try {
                            type DepositResponse = { redirectUrl: string }
                            let response: { data: DepositResponse } | null = null

                            if (provider === 'OPEN_BANKING') {
                                if (userAmount <= 0) {
                                    alert('Please enter a valid amount')
                                    return
                                }

                                response = await api.post<DepositResponse>('/deposit/open-banking', {
                                    sourceAmountMinor: moneyToMinor(displayedAmount),
                                    targetAmountMinor: moneyToMinor(targetAmountInUsd),
                                    totalSourceAmountMinor: moneyToMinor(total),
                                    sourceCurrency: 'EUR',
                                    currencyRateScaled: rateToScaled(currencyRate),
                                })
                            } else {
                                response = await api.post<DepositResponse>('/deposit/stripe', {
                                    paymentMethod: provider,
                                    country: activeCountry!,
                                    sourceCurrency: currency,
                                    currencyRateScaled: rateToScaled(currencyRate),
                                    sourceAmountMinor: moneyToMinor(displayedAmount),
                                    targetAmountMinor: moneyToMinor(targetAmountInUsd),
                                    totalSourceAmountMinor: moneyToMinor(total),
                                })
                            }

                            const redirectUrl = response?.data?.redirectUrl
                            if (redirectUrl) window.location.href = redirectUrl
                            else alert('Failed to initiate deposit. Please try again.')
                        } catch (err) {
                            console.error('Deposit failed', err)
                            alert('Failed to initiate deposit. Please try again.')
                        }
                    }}
                    className={`py-2.5 px-5 rounded-xl font-bold text-xs text-white flex items-center justify-center gap-2 transition-all ${
                        isSubmitDisabled
                            ? 'bg-neutral-300 dark:bg-neutral-800 text-neutral-500 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-500 shadow-xs cursor-pointer'
                    }`}
                >
                    <ShieldCheck size={15} />
                    <span>Pay Now</span>
                    <ArrowRight size={14} />
                </button>
            </div>
        </div>
    )
}

export default CheckoutStep